import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Activity,
  ActivityType,
  Company,
  Contact,
  Deal,
  PipelineSettings,
  Reminder,
} from '../types';

export const DEFAULT_PIPELINE_STAGES = [
  'Outreach',
  'Qualified',
  'Proposal',
  'Closed Won',
  'Closed Lost',
];

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function toDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function cleanStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function mapCompany(id: string, data: Record<string, any>): Company {
  return {
    id,
    name: cleanString(data.name),
    alignedCustomerId: cleanString(data.alignedCustomerId),
    website: cleanString(data.website),
    industry: cleanString(data.industry),
    size: cleanString(data.size),
    notes: cleanString(data.notes),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    lastActivityAt: toDateOrNull(data.lastActivityAt),
  };
}

function mapContact(id: string, data: Record<string, any>): Contact {
  return {
    id,
    firstName: cleanString(data.firstName),
    lastName: cleanString(data.lastName),
    email: cleanString(data.email),
    phone: cleanString(data.phone),
    title: cleanString(data.title),
    companyId: cleanStringOrNull(data.companyId),
    companyName: cleanStringOrNull(data.companyName),
    tags: Array.isArray(data.tags) ? data.tags.filter((v: unknown) => typeof v === 'string') : [],
    notes: cleanString(data.notes),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    lastActivityAt: toDateOrNull(data.lastActivityAt),
    createdBy: cleanString(data.createdBy),
  };
}

function mapDeal(id: string, data: Record<string, any>): Deal {
  return {
    id,
    name: cleanString(data.name),
    contactId: cleanStringOrNull(data.contactId),
    companyId: cleanStringOrNull(data.companyId),
    stage: cleanString(data.stage) || DEFAULT_PIPELINE_STAGES[0],
    value: typeof data.value === 'number' ? data.value : 0,
    expectedCloseDate: toDateOrNull(data.expectedCloseDate),
    ownerId: cleanStringOrNull(data.ownerId),
    notes: cleanString(data.notes),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

function mapActivity(id: string, data: Record<string, any>): Activity {
  const type = ['email', 'call', 'meeting', 'linkedin', 'note', 'stage_change'].includes(data.type)
    ? data.type as ActivityType
    : 'note';
  return {
    id,
    contactId: cleanString(data.contactId),
    dealId: cleanStringOrNull(data.dealId),
    type,
    subject: cleanString(data.subject),
    body: cleanString(data.body),
    activityDate: toDate(data.activityDate),
    loggedBy: cleanString(data.loggedBy),
    createdAt: toDate(data.createdAt),
  };
}

function mapReminder(id: string, data: Record<string, any>): Reminder {
  return {
    id,
    entityType: data.entityType === 'deal' ? 'deal' : 'contact',
    entityId: cleanString(data.entityId),
    assignedTo: cleanString(data.assignedTo),
    dueDate: toDate(data.dueDate),
    note: cleanString(data.note),
    completed: data.completed === true,
    createdAt: toDate(data.createdAt),
  };
}

export function subscribeToCompanies(onChange: (items: Company[]) => void, onError?: (err: Error) => void): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'companies'), orderBy('name', 'asc')),
    (snap) => onChange(snap.docs.map((d) => mapCompany(d.id, d.data()))),
    onError,
  );
}

export function subscribeToContacts(onChange: (items: Contact[]) => void, onError?: (err: Error) => void): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'contacts'), orderBy('lastName', 'asc')),
    (snap) => onChange(snap.docs.map((d) => mapContact(d.id, d.data()))),
    onError,
  );
}

export function subscribeToDeals(onChange: (items: Deal[]) => void, onError?: (err: Error) => void): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'deals'), orderBy('updatedAt', 'desc')),
    (snap) => onChange(snap.docs.map((d) => mapDeal(d.id, d.data()))),
    onError,
  );
}

export function subscribeToReminders(onChange: (items: Reminder[]) => void, onError?: (err: Error) => void): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'reminders'), orderBy('dueDate', 'asc')),
    (snap) => onChange(snap.docs.map((d) => mapReminder(d.id, d.data()))),
    onError,
  );
}

export function subscribeToActivitiesForContact(
  contactId: string,
  onChange: (items: Activity[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(
      collection(db, 'activities'),
      where('contactId', '==', contactId),
      orderBy('activityDate', 'desc'),
    ),
    (snap) => onChange(snap.docs.map((d) => mapActivity(d.id, d.data()))),
    onError,
  );
}

export function subscribeToActivitiesForDeal(
  dealId: string,
  onChange: (items: Activity[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(
      collection(db, 'activities'),
      where('dealId', '==', dealId),
      orderBy('activityDate', 'desc'),
    ),
    (snap) => onChange(snap.docs.map((d) => mapActivity(d.id, d.data()))),
    onError,
  );
}

export function subscribeToRecentActivities(
  onChange: (items: Activity[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'activities'), orderBy('activityDate', 'desc'), firestoreLimit(100)),
    (snap) => onChange(snap.docs.map((d) => mapActivity(d.id, d.data()))),
    onError,
  );
}

export function subscribeToActivities(
  onChange: (items: Activity[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'activities'), orderBy('activityDate', 'desc')),
    (snap) => onChange(snap.docs.map((d) => mapActivity(d.id, d.data()))),
    onError,
  );
}

export function subscribeToPipelineSettings(
  onChange: (settings: PipelineSettings) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'settings', 'pipeline'),
    (snap) => {
      const data = snap.data();
      const stages = Array.isArray(data?.stages) && data!.stages.length > 0
        ? data!.stages.filter((stage: unknown) => typeof stage === 'string' && stage.trim())
        : DEFAULT_PIPELINE_STAGES;
      onChange({ stages });
    },
    onError,
  );
}

export async function savePipelineStages(stages: string[]): Promise<void> {
  await setDoc(doc(db, 'settings', 'pipeline'), {
    stages: stages.map((stage) => stage.trim()).filter(Boolean),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function createCompany(data: Partial<Company>): Promise<void> {
  await addDoc(collection(db, 'companies'), {
    name: data.name ?? '',
    alignedCustomerId: data.alignedCustomerId ?? '',
    website: data.website ?? '',
    industry: data.industry ?? '',
    size: data.size ?? '',
    notes: data.notes ?? '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastActivityAt: null,
  });
}

export async function updateCompany(id: string, data: Partial<Company>): Promise<void> {
  await updateDoc(doc(db, 'companies', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCompany(id: string): Promise<void> {
  await deleteDoc(doc(db, 'companies', id));
}

export async function createContact(data: Partial<Contact>, userId: string): Promise<void> {
  await addDoc(collection(db, 'contacts'), {
    firstName: data.firstName ?? '',
    lastName: data.lastName ?? '',
    email: data.email ?? '',
    phone: data.phone ?? '',
    title: data.title ?? '',
    companyId: data.companyId ?? null,
    companyName: data.companyName ?? null,
    tags: data.tags ?? [],
    notes: data.notes ?? '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastActivityAt: null,
    createdBy: userId,
  });
}

export async function updateContact(id: string, data: Partial<Contact>): Promise<void> {
  await updateDoc(doc(db, 'contacts', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteContact(id: string): Promise<void> {
  await deleteDoc(doc(db, 'contacts', id));
}

export async function createDeal(data: Partial<Deal>): Promise<void> {
  await addDoc(collection(db, 'deals'), {
    name: data.name ?? '',
    contactId: data.contactId ?? null,
    companyId: data.companyId ?? null,
    stage: data.stage ?? DEFAULT_PIPELINE_STAGES[0],
    value: data.value ?? 0,
    expectedCloseDate: data.expectedCloseDate ?? null,
    ownerId: data.ownerId ?? null,
    notes: data.notes ?? '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateDeal(id: string, data: Partial<Deal>): Promise<void> {
  await updateDoc(doc(db, 'deals', id), { ...data, updatedAt: serverTimestamp() });
}

export async function moveDealToStage(id: string, stage: string): Promise<void> {
  await updateDoc(doc(db, 'deals', id), { stage, updatedAt: serverTimestamp() });
}

export async function deleteDeal(id: string): Promise<void> {
  await deleteDoc(doc(db, 'deals', id));
}

export async function createActivity(data: Partial<Activity>, userId: string): Promise<void> {
  await addDoc(collection(db, 'activities'), {
    contactId: data.contactId ?? '',
    dealId: data.dealId ?? null,
    type: data.type ?? 'note',
    subject: data.subject ?? '',
    body: data.body ?? '',
    activityDate: data.activityDate ?? new Date(),
    loggedBy: userId,
    createdAt: serverTimestamp(),
  });
}

export async function createReminder(data: Partial<Reminder>, userId: string): Promise<void> {
  await addDoc(collection(db, 'reminders'), {
    entityType: data.entityType ?? 'contact',
    entityId: data.entityId ?? '',
    assignedTo: data.assignedTo ?? userId,
    dueDate: data.dueDate ?? new Date(),
    note: data.note ?? '',
    completed: data.completed ?? false,
    createdAt: serverTimestamp(),
  });
}

export async function updateReminder(id: string, data: Partial<Reminder>): Promise<void> {
  await updateDoc(doc(db, 'reminders', id), { ...data });
}

export async function deleteReminder(id: string): Promise<void> {
  await deleteDoc(doc(db, 'reminders', id));
}

export async function fetchContact(id: string): Promise<Contact | null> {
  const snap = await getDoc(doc(db, 'contacts', id));
  return snap.exists() ? mapContact(snap.id, snap.data()) : null;
}

export async function fetchCompany(id: string): Promise<Company | null> {
  const snap = await getDoc(doc(db, 'companies', id));
  return snap.exists() ? mapCompany(snap.id, snap.data()) : null;
}

export async function fetchDeal(id: string): Promise<Deal | null> {
  const snap = await getDoc(doc(db, 'deals', id));
  return snap.exists() ? mapDeal(snap.id, snap.data()) : null;
}
